import { onBeforeUnmount } from 'vue'
import { realtimeEventSchema, type RealtimeEvent } from '@threadline/shared'
import { buildAuthorizationHeaders } from '../api/activeAccount.ts'

type RealtimeEventListener = (realtimeEvent: RealtimeEvent) => void
type ConnectionRestoredListener = () => void

const realtimeEventListeners = new Set<RealtimeEventListener>()
const connectionRestoredListeners = new Set<ConnectionRestoredListener>()

const RECONNECT_DELAY_MS = 3_000

let activeStreamAbortController: AbortController | null = null
let hasConnectedAtLeastOnce = false

function announceRealtimeEvent(serializedEvent: string): void {
  let decodedEvent: unknown
  try {
    decodedEvent = JSON.parse(serializedEvent)
  } catch {
    return
  }

  const parsedEvent = realtimeEventSchema.safeParse(decodedEvent)
  if (!parsedEvent.success) {
    return
  }
  for (const realtimeEventListener of [...realtimeEventListeners]) {
    realtimeEventListener(parsedEvent.data)
  }
}

function announceConnectionRestored(): void {
  if (hasConnectedAtLeastOnce) {
    for (const connectionRestoredListener of [...connectionRestoredListeners]) {
      connectionRestoredListener()
    }
  }
  hasConnectedAtLeastOnce = true
}

/**
 * Reads one Server-Sent Events frame at a time out of the response body.
 *
 * Only the `data:` lines matter here: every event this application sends carries its own
 * type inside the payload, and comments (`:` lines, which is what the heartbeat is) are
 * skipped exactly as the specification says.
 */
function consumeEventStreamFrame(streamFrame: string): void {
  const dataLines = streamFrame
    .split('\n')
    .filter((frameLine) => frameLine.startsWith('data:'))
    .map((frameLine) => frameLine.slice('data:'.length).trim())

  if (dataLines.length > 0) {
    announceRealtimeEvent(dataLines.join('\n'))
  }
}

async function readEventStream(streamResponse: Response, abortSignal: AbortSignal): Promise<void> {
  const streamReader = streamResponse.body?.getReader()
  if (streamReader === undefined) {
    return
  }

  const streamDecoder = new TextDecoder()
  let pendingText = ''

  while (!abortSignal.aborted) {
    const { value: streamChunk, done: streamEnded } = await streamReader.read()
    if (streamEnded) {
      return
    }

    pendingText += streamDecoder.decode(streamChunk, { stream: true })
    // Frames are separated by a blank line; anything after the last one is incomplete.
    const streamFrames = pendingText.split('\n\n')
    pendingText = streamFrames.pop() ?? ''
    for (const streamFrame of streamFrames) {
      consumeEventStreamFrame(streamFrame)
    }
  }
}

/**
 * `EventSource` cannot send an authorization header, and the session token must never
 * travel in a URL where it would land in an access log. The stream is therefore read with
 * `fetch`, which also means the reconnection policy is ours: it reconnects on its own and
 * tells listeners so they refetch state rather than trusting events they may have missed.
 */
export function startRealtimeEventStream(): void {
  if (activeStreamAbortController !== null) {
    return
  }

  const streamAbortController = new AbortController()
  activeStreamAbortController = streamAbortController
  void keepEventStreamOpen(streamAbortController)
}

async function keepEventStreamOpen(streamAbortController: AbortController): Promise<void> {
  while (!streamAbortController.signal.aborted) {
    try {
      const streamResponse = await fetch('/api/events/stream', {
        headers: { ...buildAuthorizationHeaders(), accept: 'text/event-stream' },
        signal: streamAbortController.signal,
      })

      if (streamResponse.ok) {
        announceConnectionRestored()
        await readEventStream(streamResponse, streamAbortController.signal)
      }
    } catch {
      // A dropped connection is expected; the wait below is what keeps it from spinning.
    }

    if (streamAbortController.signal.aborted) {
      return
    }
    await new Promise((resolveDelay) => setTimeout(resolveDelay, RECONNECT_DELAY_MS))
  }
}

export function stopRealtimeEventStream(): void {
  activeStreamAbortController?.abort()
  activeStreamAbortController = null
  hasConnectedAtLeastOnce = false
}

export function useRealtimeEvents(handlers: {
  onRealtimeEvent?: RealtimeEventListener
  onConnectionRestored?: ConnectionRestoredListener
}) {
  if (handlers.onRealtimeEvent !== undefined) {
    realtimeEventListeners.add(handlers.onRealtimeEvent)
  }
  if (handlers.onConnectionRestored !== undefined) {
    connectionRestoredListeners.add(handlers.onConnectionRestored)
  }

  onBeforeUnmount(() => {
    if (handlers.onRealtimeEvent !== undefined) {
      realtimeEventListeners.delete(handlers.onRealtimeEvent)
    }
    if (handlers.onConnectionRestored !== undefined) {
      connectionRestoredListeners.delete(handlers.onConnectionRestored)
    }
  })
}
